import { defineComponent, reactive, SetupContext, readonly, ref, computed, onMounted } from '@vue/composition-api';
import { MaterialDetailType, MaterialDetailFile } from '@/shared/type';
import SystemConst from '@/shared/system-const';
import HttpStatus from 'http-status-codes';
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';
import CommonMaterialFilter from '@/components/common/common-material-filter.component.vue';
import { SearchConditionType, InputConditionType } from '@/components/common/common-material-filter.component';
import vueRouter from '@/router/index';

// リアクティブな変数の型定義
export type reactiveData = {
    windowSize: { x: number; y: number };                   // Windowサイズ
    schoolTypes: { id: string; name: string }[];            // 校種一覧（タブ一覧）
    isTabLoading: boolean;                                  // タブ一覧取得のロード中: true ロード中でない: false
    searchConditions: SearchConditionType;                  // バックから取得した検索条件
    selectAllSchoolTypes: [],                               // 校種の選択状態
    selectGrades: [],                                       // 学年の選択状態
    selectCurriculums: [],                                  // 教科・科目の選択状態
    selectMaterialTypes: [],                                // 教材の種類の選択状態
    keyword: null,                                          // 検索条件のキーワード
    detail: MaterialDetailType,
    materialDbFiles: MaterialDetailFile[]
    isSummaryDisplay: boolean,
    isUrlDisplay: boolean,
    isFileDisplay: boolean,
    isGradeDisplay: boolean,
    isCurriculumDisplay: boolean,
    isSearchKeywordDisplay: boolean,
    isOrganizationDisplay: boolean,
    isNameDisplay: boolean,
    isMailDisplay: boolean,
    isUpdateAtDisplay: boolean
    isCopyrightDisplay: boolean,
    isOperationEnvDisplay: boolean,
    isRemarksDisplay: boolean,
    tab: string
}

/**
 * 教材DB詳細画面コンポーネント
 */
export default defineComponent({
    components: { CommonMessageDialog, CommonMaterialFilter },
    setup(props, ctx: SetupContext) {
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            windowSize: { x: 0, y: 0 },                         // ウィンドウサイズ
            schoolTypes: [],                                    // 校種一覧（タブ一覧）
            isTabLoading: true,                                 // タブ一覧取得のロード中: true ロード中でない: false
            searchConditions: {
                schoolTypes: [],
                materialTypes: []
            },                                                  // バックから取得した検索条件
            selectAllSchoolTypes: [],                           // 校種の選択状態
            selectGrades: [],                                   // 学年の選択状態
            selectCurriculums: [],                              // 教科・科目の選択状態
            selectMaterialTypes: [],                            // 教材の種類の選択状態
            keyword: null,                                      // 検索条件のキーワード
            detail: {
                title: '',                                      // タイトル
                summary: '',                                    // 概要
                urls: [],                                       // 教材URL
                files: [],                                      // 教材ファイル
                schoolTypeName: "",                             // 対象校種
                gradeNames: [],                                 // 学年
                gradeNamesDisplay: "",                          // 学年（表示用）
                curriculums: [],                                // 教科・科目
                curriculumsDisplay: "",                         // 教科・科目（表示用）
                organizationName: '',                           // 所属
                name: '',                                       // 代表者
                mail: '',                                       // メールアドレス
                updateAt: '',                                   // 更新日
                searchKeyword: '',                              // 検索キーワード
                publicationRangeName: '',                       // 公開範囲
                useTermsName: '',                               // 利用条件
                copyright: '',                                  // 著作
                operationEnvironment: '',                       // 動作環境
                remarks: ''                                     // 備考
            },
            materialDbFiles: [],
            isSummaryDisplay: false,                            // 表示フラグ（概要）
            isUrlDisplay: false,                                // 表示フラグ（教材URL）
            isFileDisplay: false,                               // 表示フラグ（教材ファイル）
            isGradeDisplay: false,                              // 表示フラグ（学年）
            isCurriculumDisplay: false,                         // 表示フラグ（教科・科目）
            isSearchKeywordDisplay: false,                      // 表示フラグ（検索キーワード）
            isOrganizationDisplay: false,                       // 表示フラグ（所属）
            isNameDisplay: false,                               // 表示フラグ（氏名）
            isMailDisplay: false,                               // 表示フラグ（メールアドレス）
            isUpdateAtDisplay: false,                           // 表示フラグ（更新日）
            isCopyrightDisplay: false,                          // 表示フラグ（著作）
            isOperationEnvDisplay: false,                       // 表示フラグ（動作環境）
            isRemarksDisplay: false,                            // 表示フラグ（備考）
            tab: ''                                             // タブの選択状態（校種ID格納）
        });
        const commonRef = ref<any>(null);                       // メッセージダイアログ
        const materialId
            = computed(() => ctx.root.$route.params.id).value;  // 教材ID
        const fileIconList = SystemConst.FILE_EXTENSION_ICON;   // 添付ファイル拡張子アイコンリスト
        const filterMaterialRef = ref<any>(null);               // 検索条件のDOM
        const router = vueRouter;                               // ルータ
        const circularSize
            = SystemConst.MATERIAL_DB_LOADING_CIRCULAR.SIZE;    // 校種情報取得中のローダーサイズ
        const circularWidth
            = SystemConst.MATERIAL_DB_LOADING_CIRCULAR.WIDTH;   // 校種情報取得中のローダー幅

        /**
         * 初期処理
         */
        onMounted(() => {
            init();
        });

        async function init() {
            // 教材検索 フィルタ条件取得APIで検索条件を取得する
            const response = await ctx.root.$store.dispatch('materialDb/getFilterConditions');

            // HTTPステータスコードが200以外の場合、エラー画面へ遷移して処理を終了する
            if (response.status !== HttpStatus.OK) {
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 検索条件をcommon-material-filterコンポーネントへ設定する
            state.searchConditions = {
                schoolTypes: response.data.schoolTypes,
                materialTypes: response.data.materialTypes
            };
        }

        /**
         * 初期表示
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-database-search');

            // mainTitleを「教材詳細」に変更
            ctx.root.$store.commit('setMainTitle', '教材詳細');

            // ストアのschoolTypesTab に校種タブ情報が格納されていないことを判別する
            if (ctx.root.$store.state.materialDb.schoolTypesTab.length === 0) {
                // タブ情報がストアにない場合、タブ情報取得処理呼び出し
                await getSchoolTypesTab();
            } else {
                // タブ情報がある場合、ストアから復元する
                state.schoolTypes = ctx.root.$store.state.materialDb.schoolTypesTab;
            }

            // ローダーの表示を解除する
            state.isTabLoading = false;

            // 教材詳細取得APIで初期表示データを取得する
            const response = await ctx.root.$store.dispatch('materialDb/getMaterialDetail',
                {
                    materialId: materialId
                }
            );

            // HTTPステータスコードが200以外の場合
            if (response.status !== HttpStatus.OK) {
                if (response.status === HttpStatus.NOT_FOUND || response.status === HttpStatus.BAD_REQUEST) {
                    // 400, 404エラーの場合、エラーメッセージを表示して教材一覧画面へ戻る
                    commonRef.value.displayErrorMessageTransitionDialog(response, '/material-db/list');
                    return;
                }
                // その他のエラーの場合、エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // 取得した校種情報で、タブを切り替える
            // store に保存されている校種ID（選択中のタブ）が「全校種(0)」の場合、選択するタブは「全校種」
            if (ctx.root.$store.state.materialDb.schoolTypeId === SystemConst.SCHOOL_TYPE_ID.ALL_SCHOOL_TYPES) {
                state.tab = ctx.root.$store.state.materialDb.schoolTypeId;
            } else {
                state.tab = String(response.data.materialDetail.schoolTypeId);
            }

            // タブの情報を保存する
            ctx.root.$store.commit('materialDb/setSchoolTypeId', state.tab);
            // 取得した校種情報を削除する
            delete response.data.materialDetail.schoolTypeId;

            // 画面項目に教材詳細データを設定する
            state.detail = response.data.materialDetail;

            // 学年の配列をカンマ区切りの文字列に変換して表示用項目に設定する
            state.detail.gradeNamesDisplay = response.data.materialDetail.gradeNames.join('、');

            // 教科・科目のオブジェクトを以下のようなカンマ区切り文字列に変換して表示用項目に設定する
            // 教科1（科目1、科目2 ...）、教科2（科目1、科目2 ...）...
            let curriculumsDisplay = '';
            for (let i = 0; i < response.data.materialDetail.curriculums.length; i++) {

                // 科目をカンマ区切りにして教科の後ろに表示する
                if (response.data.materialDetail.curriculums[i].subjectNames.length !== 0) {
                    curriculumsDisplay += response.data.materialDetail.curriculums[i].curriculumName +
                        '(' + response.data.materialDetail.curriculums[i].subjectNames.join('、') + ')';
                } else {
                    // 教科に該当する科目が未登録の場合は、教科のみ出力する
                    curriculumsDisplay += response.data.materialDetail.curriculums[i].curriculumName;
                }
                // 教科をカンマ区切りにする
                if (i !== response.data.materialDetail.curriculums.length - 1) {
                    curriculumsDisplay += '、';
                }
            }

            state.detail.curriculumsDisplay = curriculumsDisplay;

            // 添付ファイルデータを取得した場合、以下を添付ファイルの数分繰り返す
            const filesNumber = response.data.materialDetail.files.length;
            if (filesNumber !== 0) {
                for (let i = 0; i <= filesNumber - 1; i++) {
                    const materialFile: MaterialDetailFile[] = [ // 一時保存するファイル情報
                        {
                            fileIcon: '',
                            fileSequence: 0,
                            fileName: '',
                            fileSize: ''
                        }
                    ];
                    materialFile[0].fileSequence = response.data.materialDetail.files[i].fileSequence;
                    materialFile[0].fileName = response.data.materialDetail.files[i].fileName;

                    // ファイル名から拡張子部分を取得する
                    const extension = materialFile[0].fileName.substring(
                        materialFile[0].fileName.lastIndexOf('.') + 1);

                    // アイコンのリストを作成する
                    const fileIcon = Object.keys(fileIconList);
                    // 添付ファイルアイコンを拡張子から判別し、設定する
                    Object.values(fileIconList).forEach((element, index) => {
                        // 添付ファイルの拡張子を小文字に変換して比較する
                        const iconList = element.some((extensionList) => extensionList === extension.toLowerCase());
                        if (iconList === true) {
                            // 拡張子を見つけたときの添え字番号で検索し、設定する
                            materialFile[0].fileIcon = fileIcon[index];
                        }
                        if (materialFile[0].fileIcon === '' && fileIcon.length - 1 === index) {
                            // 最後まで検索し、一致する拡張子がなかった場合
                            materialFile[0].fileIcon = fileIcon[index];
                        }
                    });

                    // 添付ファイルのサイズを「B(バイト)」表記から「KB(キロバイト)」表記へ変換する
                    const fileSize = String(Math.round((response.data.materialDetail.files[i].fileSize / 1024) * 1000) / 1000);
                    materialFile[0].fileSize = fileSize;

                    state.materialDbFiles.push(materialFile[0]);
                }
            }

            // 更新日を表示用に変換する
            if (state.detail.updateAt) {
                inputDate(state.detail.updateAt);
            }

            // 画面項目表示設定を行う
            setDisplayFlg(state.detail);
        }

        /**
         * 校種設定タブ情報取得処理
         */
        async function getSchoolTypesTab() {
            // 校種タブ取得用APIを呼び出す
            // パラメータ: なし
            const response = await ctx.root.$store.dispatch('materialDb/getSchoolTypesTab');

            // 通信結果判別
            if (response.status !== HttpStatus.OK) {
                // HTTPステータスコードが200以外の場合
                // エラー画面へ遷移して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);
                return;
            }

            // レスポンスデータをstate.schoolTypes に設定
            state.schoolTypes = response.data.schoolTypes;

            // 校種タブをストアに保存する
            ctx.root.$store.commit('materialDb/setSchoolTypesTab', state.schoolTypes);
        }

        /**
         * 日付を表示用にフォーマット変換する
         * 
         * @param dateValue 日付文字列
         */
        function inputDate(dateValue: string) {
            // YYYY-MM-DD ⇒ YYYY/MM/DD
            state.detail.updateAt = dateValue.replaceAll('-', '/').slice(0, 10);
        }

        /**
         * 画面項目の表示フラグを設定する
         * 
         * @param detail 日付文字列
         */
        function setDisplayFlg(detail: MaterialDetailType) {

            // 概要
            if (detail.summary) {
                state.isSummaryDisplay = true;
            }

            // 教材情報
            // 教材URL
            if (detail.urls.length !== 0) {
                state.isUrlDisplay = true;
            }

            // 教材ファイル
            if (detail.files.length !== 0) {
                state.isFileDisplay = true;
            }

            // 利用対象
            // 学年
            if (detail.gradeNames.length !== 0) {
                state.isGradeDisplay = true;
            }

            // 教科・科目
            if (detail.curriculums.length !== 0) {
                state.isCurriculumDisplay = true;
            }

            // 登録者情報
            // 検索キーワード
            if (detail.searchKeyword) {
                state.isSearchKeywordDisplay = true;
            }

            // 所属
            if (detail.organizationName) {
                state.isOrganizationDisplay = true;
            }

            // 氏名（代表者）
            if (detail.name) {
                state.isNameDisplay = true;
            }

            // メールアドレス
            if (detail.mail) {
                state.isMailDisplay = true;
            }

            // 更新日
            if (detail.updateAt) {
                state.isUpdateAtDisplay = true;
            }

            // 詳細情報
            // 著作
            if (detail.copyright) {
                state.isCopyrightDisplay = true;
            }

            // 作業環境
            if (detail.operationEnvironment) {
                state.isOperationEnvDisplay = true;
            }

            // 備考
            if (detail.remarks) {
                state.isRemarksDisplay = true;
            }
        }

        /**
         * 教材ファイルダウンロード処理
         * @param fileSequence ファイルシーケンス番号
         */
        async function fileDownload(fileSequence: number) {
            // 教材ファイルダウンロードAPIを呼び出す
            // GET /api/material-db/{materialId}/{fileSequence}
            // パラメータ：教材ID
            //            ファイルシーケンス番号
            const response = await ctx.root.$store.dispatch('materialDb/download',
                {
                    materialId: materialId,
                    fileSequence: fileSequence
                }
            );

            // API通信成否判定
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);
            }
            return;
        }

        /**
         * ストアから値を取得して、画面遷移前の検索条件を復元する
         * 教材検索コンポーネントの初期表示時とタブ切り替え時のonMountedから呼ばれる
         * 
         * @param tabId 選択されたタブID
         */
        function restoreFilter(tabId: string) {
            // 遷移前に選択していたタブがマウントされた場合のみ検索条件を復元する
            if (tabId === ctx.root.$store.state.materialDb.schoolTypeId) {
                // 校種
                state.selectAllSchoolTypes = ctx.root.$store.state.materialDb.allSchoolTypes;
                // 学年
                state.selectGrades = ctx.root.$store.state.materialDb.grades;
                // 教科・科目
                state.selectCurriculums = ctx.root.$store.state.materialDb.curriculums;
                // 教材の種類
                state.selectMaterialTypes = ctx.root.$store.state.materialDb.materialTypes;
                // キーワード
                state.keyword = ctx.root.$store.state.materialDb.keyword;
            }
        }

        /**
         * フィルタ設定によるイベント
         * 検索ボタン押下時に呼び出される
         * 
         * @param inputConditions 入力された検索条件
         */
        function searchFilter(inputConditions: InputConditionType) {
            // 検索条件をストアへ保存する
            // 校種タブID
            ctx.root.$store.commit('materialDb/setSchoolTypeId', String(inputConditions.id));
            // 校種
            ctx.root.$store.commit('materialDb/setAllSchoolTypes', inputConditions.allSchoolTypes);
            // 学年
            ctx.root.$store.commit('materialDb/setGrades', inputConditions.grades);
            // 教科・科目
            ctx.root.$store.commit('materialDb/setCurriculums', inputConditions.curriculums);
            // 教材の種類
            ctx.root.$store.commit('materialDb/setMaterialTypes', inputConditions.materialTypes);
            // キーワード
            ctx.root.$store.commit('materialDb/setKeyword', inputConditions.keyword);
            // 教材一覧画面へ遷移する
            router.push('/material-db/list');
        }

        /**
         * タブクリックイベント
         */
        function onClickTab(id: string) {
            // 選択した校種をストアへ保存する
            ctx.root.$store.commit('materialDb/setSchoolTypeId', id);

            // 選択した校種タブ以外の復元は不要のため、下記のストアに保持してある値をリセットする
            // 校種
            ctx.root.$store.commit('materialDb/setAllSchoolTypes', []);
            // 学年
            ctx.root.$store.commit('materialDb/setGrades', []);
            // 教科・科目
            ctx.root.$store.commit('materialDb/setCurriculums', []);
            // 教材の種類
            ctx.root.$store.commit('materialDb/setMaterialTypes', []);
            // キーワード
            ctx.root.$store.commit('materialDb/setKeyword', '');
            // 1ページ毎の行数
            ctx.root.$store.commit('materialDb/setItemsPerPage', 0);
            // ページ番号
            ctx.root.$store.commit('materialDb/setPageNation', 0);
            // ソート順
            ctx.root.$store.commit('materialDb/setSortOrder', '');
            // 編集・削除ボタン表示フラグ
            ctx.root.$store.commit('materialDb/setIsDisplayButton', false);
            // サムネイル表示フラグ
            ctx.root.$store.commit('materialDb/setIsDisplayThumbnail', true);

            // 教材一覧画面へ遷移する
            router.push('/material-db/list');
        }

        /**
         * URLクリックイベント
         */
        function onClickUrl(url: string) {
            // window.openを使用してURLのページを別タブで開く
            window.open(url, '_blank')
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };

            // 教材検索条件コンポーネントのサイズも変更（タブにより複数あるコンポーネントに対し変更する）
            filterMaterialRef.value.forEach((x: any) => x.resize(state.windowSize));
        }

        /**
         * 戻るボタン押下時の処理
         */
        function back() {
            // 遷移元画面へ遷移する
            router.back();
        }

        return {
            state: readonly(state),
            commonRef,
            filterMaterialRef,
            circularSize,
            circularWidth,
            fileDownload,
            onClickTab,
            onClickUrl,
            restoreFilter,
            searchFilter,
            onResize,
            back
        };
    }
});
